import { Component, inject, OnInit } from '@angular/core';
import { CalendarModule } from 'primeng/calendar';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { AutoCompleteCompleteEvent, AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { MentorDashboardService } from '../../services/mentor-dashboard/mentor-dashboard.service';
import { Router } from '@angular/router';
import { MentorStudentResponse } from '../../models/account/mentor/mentorStudentsResponse';
import { apiResponse } from '../../models/apiResponse';
import { MessageService } from 'primeng/api';
import { countryResponse } from '../../models/country/countryResponse';
import { forkJoin } from 'rxjs';
import { UtilityService } from '../../services/utility/utility.service';
import { ScheduleMentorCallMeetingnotesService } from '../../services/schedule-mentor-call-meetingnotes/schedule-mentor-call-meetingnotes.service';
import { SearchByEmailResponse } from '../../models/account/searchByEmailResponse';
import { DialogModule } from 'primeng/dialog';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../states/user/user-selector';
import { UserStateModel } from '../../states/user/user-state-model';
import { MentorNotesForStudentResponse } from '../../models/mentor-notes-for-student/mentorNotesForStudentResponse';
import { SearchByNameResponse } from '../../models/account/searchByNameResponse';
import { EditorModule } from 'primeng/editor';
import { TooltipModule } from 'primeng/tooltip';


@Component({
  selector: 'app-mentor-dashboard',
  standalone: true,
  imports: [CalendarModule, CommonModule, FormsModule, InputTextModule, ButtonModule, TableModule, DialogModule, AutoCompleteModule, EditorModule, TooltipModule],
  templateUrl: './mentor-dashboard.component.html',
  styleUrl: './mentor-dashboard.component.scss'
})
export class MentorDashboardComponent implements OnInit {
  user!: UserStateModel;
  totalRecords: number = 0;
  mentorStudents: MentorStudentResponse[] = []
  fromDate: Date | null = null;
  toDate: Date | null = null;
  studentEmail: string | null = null;
  studentName: any = null;
  studentNameSuggestions: SearchByNameResponse[] = [];
  studentEmailSuggestions: SearchByEmailResponse[] = [];
  isSearchingNames: boolean = false;
  first: number = 0;
  rows: number = 10;
  isLoading: boolean = false;
  visible: boolean = false;
  mentorNote: MentorNotesForStudentResponse | undefined;
  noteText: string = '';
  currentStudentId: string | undefined;
 
  addNoteVisible: boolean = false;
  newNoteText: string = '';
  newNoteDate: Date | null = null;
  viewNotesVisible: boolean = false;
  studentNotesList: { id: string; noteText: string; noteDate: string }[] = [];
  isLoadingNotes: boolean = false;
  countries: countryResponse[] = [];

  constructor(
    private mentorDashboardService: MentorDashboardService,
    private router: Router,
    private messageService: MessageService,
    private utilityService: UtilityService,
    private meetingnotesService: ScheduleMentorCallMeetingnotesService,
    private strore: Store,
  ) { }

  ngOnInit(): void {
    this.getUser();
    // this.getStudents(1);
    this.getUsersAfterDataLoaded();
  }

  openUrl(url: string): void {
    window.open(url, '_blank');
  }

  getUser() {
    this.strore.select(UserSelectors.getUser).subscribe({
      next: (response) => {
        this.user = response;
      },
      error: (error) => {
        console.warn('Error fetching user:', error);
      }
    })
  }
  getCountryName(arg0: string | null | undefined) {
    return this.countries.find(item => item.countryCode == arg0)?.name;
  }

  getCountries(arg0: string[] | undefined): string {
    if (!arg0) {
      return '';  // If arg0 is undefined or empty, return an empty string
    }

    const countryNames = arg0
      .map(item => {
        const country = this.countries.find(country => country.countryCode === item);
        return country ? country.name : undefined;  // Return country name or undefined
      })
      .filter(name => name !== undefined);  // Filter out any undefined values

    return countryNames.join(',');  // Join the country names with a comma
  }

openAddNoteDialog(studentId: string) {
  this.currentStudentId = studentId;
  this.newNoteText = '';
  this.newNoteDate = null;
  this.addNoteVisible = true;
}

openViewNotesDialog(studentId: string) {
  this.currentStudentId = studentId;
  this.viewNotesVisible = true;
  this.studentNotesList = [];
  this.isLoadingNotes = true;

  this.meetingnotesService.getMentorNotesForStudent(studentId).subscribe({
    next: (response: any) => {
      // Assuming API returns array sorted by date desc
      this.studentNotesList = response || [];
      this.isLoadingNotes = false;
    },
    error: (err) => {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'No notes found!' });
      this.isLoadingNotes = false;
    }
  });
}

saveNewNote() {
  if (!this.newNoteText || !this.newNoteDate) return;

  this.meetingnotesService.addMentorNotesForStudent({
    studentId: this.currentStudentId!,
    mentorNotesForStudent: this.newNoteText,
    noteDate: this.newNoteDate!.toISOString()
  }).subscribe({
    next: (response: any) => {
      this.studentNotesList = (response || []).sort(
        (a: any, b: any) => new Date(b.noteDate).getTime() - new Date(a.noteDate).getTime()
      );
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Note added successfully!' });
      this.addNoteVisible = false;
      this.newNoteText = '';
    },
    error: (err) => {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add note!' });
    }
  });
}

deleteNote(noteId: string) {
  this.meetingnotesService.deleteMentorNotesForStudent(this.currentStudentId!, noteId).subscribe({
    next: () => {
      this.studentNotesList = this.studentNotesList.filter(n => n.id !== noteId);
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Note deleted!' });
    },
    error: (err) => {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete note!' });
    }
  });
}

formatNoteDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

  deleteMeetingNotes() {
    this.meetingnotesService.deleteMentorNotesForStudent(this.currentStudentId!, '').subscribe({
      next: (response) => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Student notes deleted successfully!' });
        this.mentorNote = undefined;
        this.mentorStudents.find(m => m.id === this.currentStudentId)!.mentorNotesForStudent = '';
        this.noteText = '';
        this.currentStudentId = undefined;
        this.visible = false;
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete Student notes!' });
        console.error(err);
      }
    })
  }

  addMeetingNotes() {
    this.meetingnotesService.addMentorNotesForStudent({
      studentId: this.currentStudentId!,
      mentorNotesForStudent: this.noteText
    }).subscribe({
      next: (response) => {
        this.mentorNote = response;
        this.mentorStudents.find(m => m.id === this.currentStudentId)!.mentorNotesForStudent = this.noteText;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Student notes updated successfully!' });
        this.visible = false;
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update Student notes!' });
        console.error(err);
      }
    })
  }

  getUsersAfterDataLoaded() {
    forkJoin([
      this.utilityService.getCountries(),

    ]).subscribe({
      next: ([countriesResponse]) => {
        this.countries = countriesResponse.result;  // Set countries
        this.getStudents(1);
      },
      error: (err) => console.warn("Error loading data:", err),
    });
  }

  getStudents(pageNumber: number) {
    this.isLoading = true;
    this.mentorDashboardService.searchMentorStudents({
      FromDate: this.fromDate ? this.fromDate.toISOString() : null,
      ToDate: this.toDate ? this.toDate.toISOString() : null,
      StudentEmail: typeof this.studentEmail === 'string' ? this.studentEmail : (this.studentEmail as any)?.email || null,
      StudentName: typeof this.studentName === 'string' ? this.studentName : (this.studentName as any)?.name || null,
      PageNumber: pageNumber,
      PageSize: this.rows
    }).subscribe({
      next: (response: apiResponse<MentorStudentResponse[]>) => {
        if (response) {
          this.mentorStudents = response.result;
          this.totalRecords = response.total;
          this.isLoading = false;
        } else {
          this.mentorStudents = [];
          this.totalRecords = 0;
          this.isLoading = false;
        }
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching mentor students' });
      }
    });
  }

  onLazyLoad($event: TableLazyLoadEvent): void {
    this.first = $event.first ?? 0;
    this.rows = $event.rows ?? this.rows;
    const pageNumber = Math.floor(this.first / this.rows) + 1;
    this.getStudents(pageNumber);
  }

  onSearch() {
    this.first = 0;
    this.getStudents(1);
  }


  viewProfile(studentId: string): void {
    this.router.navigate(['/mentor-portal', 'app-applicant-profile', studentId]);
  }


  onStudentNameQuery(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchNamesByRole({
        roleName: 'Student',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.studentNameSuggestions = response.result
        }
      });
    } else {
      this.studentNameSuggestions = [];
    }
  }

  onStudentEmailQuery(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchEmailsByRole({
        roleName: 'Student',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.studentEmailSuggestions = response.result;
        }
      });
    } else {
      this.studentEmailSuggestions = [];
    }
  }
}
